import React, { useState, useEffect } from 'react';
import useRepo from '../../hooks/useRepo';
import useApiContext from 'hooks/useApiContext';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { T, M, A } from 'TOMA';
import { useForm, Controller } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { InputText, InputSelect } from '../../form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import routes from 'router/routes';
import TemplateOne from '../../assets/template-image/TemplateOne.png';
import TemplateTwo from '../../assets/template-image/TemplateTwo.png';
import TemplateThree from '../../assets/template-image/TemplateThree.png';

function SubCategoryFormPage(): JSX.Element {
  const { categoryRepository } = useApiContext();
  const { categoryId } = useParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [loadingForm, setLoadingForm] = useState(false);
  const [successForm, setSuccessForm] = useState(false);
  const [errorForm, setErrorForm] = useState<string>();
  const [categories, setCategories] = useState<any[]>([]);
  const [selectedTemplate, setSelectedTemplate] =
    useState<string>('template_one');
  const { t } = useTranslation();

  // Template configuration with images and descriptions
  const templateData = {
    template_one: {
      value: 'template_one',
      label: t('categories:template_one'),
      image: TemplateOne,
    },
    template_two: {
      value: 'template_two',
      label: t('categories:template_two'),
      image: TemplateTwo,
    },
    template_three: {
      value: 'template_three',
      label: t('categories:template_three'),
      image: TemplateThree,
    },
  };
  const { response, loading } = useRepo(async () => {
    if (categoryId && categoryId !== 'new-subcategory') {
      return await categoryRepository.getCategoryById(categoryId);
    }
    return null;
  }, [categoryId, pathname]);

  const { response: parentCategories } = useRepo(async () => {
    return await categoryRepository.parentCategory('category');
  }, [pathname]);

  useEffect(() => {
    if (parentCategories) {
      setCategories(parentCategories);
    }
  }, [parentCategories]);

  const schema = yup.object({
    topicCode: yup.string().max(50).required(t('common:form.error.required')),
    code: yup.string().max(50).required(t('common:form.error.required')),
    label: yup.string().max(255).required(t('common:form.error.required')),
    parent_id: yup.mixed().required(t('common:form.error.required')),
    type: yup.mixed().required(t('common:form.error.required')),
  });
  const form = useForm<any>({
    mode: 'onChange',
    reValidateMode: 'onBlur',
    resolver: yupResolver(schema),
    defaultValues: {
      topicCode: '',
      code: '',
      label: '',
      filter: '',
      label_en: '',
      category: null,
      parent_id: null,
      type: null,
    },
  });

  const {
    handleSubmit,
    control,
    reset,
    getValues,
    watch,
    formState: { errors },
  } = form;

  // Watch for changes in the type field to update preview
  const watchedType = watch('type');
  useEffect(() => {
    if (watchedType && templateData[watchedType as keyof typeof templateData]) {
      setSelectedTemplate(watchedType);
    }
  }, [watchedType]);

  useEffect(() => {
    if (categoryId && !loading && response && categories.length > 0) {
      // Find the parent category from the response
      const parentCategory = categories.find(
        (cat) => cat.id === response.parent_id
      );

      // Set the category field value
      const categoryValue = parentCategory
        ? {
            value: parentCategory.id,
            label: parentCategory.label,
          }
        : null;

      reset({
        topicCode: response.topicCode,
        code: response.code,
        label: response.label,
        filter: response.filter,
        label_en: response.label_en,
        type: response.type ?? 'template_one',
        parent_id: {
          value: response.parent_id ?? '',
          label:
            categories.find((cat) => cat.id === response.parent_id)?.label ??
            '',
        },
      });

      // Set the selected template for preview
      setSelectedTemplate(response.type ?? 'template_one');
    }
  }, [loading, response, reset, categoryId, categories]);

  function onSubmit(formData: any): void {
    setLoadingForm(true);
    setErrorForm('');
    setSuccessForm(false);

    // Transform the form data for API submission
    const apiData = {
      topic: 'type_produit',
      topicCode: formData.topicCode,
      code: formData.code,
      label: formData.label,
      filter: formData.filter,
      label_en: formData.label_en,
      parent_id: formData.parent_id?.value || formData.parent_id || null,
      type: formData.type,
    };

    if (categoryId && categoryId !== 'new-subcategory') {
      categoryRepository
        .updateCategory(categoryId, apiData)
        .then(() => {
          toast.success(t('categories:update_success'));
          setSuccessForm(true);
          setTimeout(() => {
            navigate(routes.subcategories.path);
          }, 1500);
        })
        .catch((err: Error) => {
          setErrorForm(err.message);
          toast.error(err.message || t('categories:update_error'));
        })
        .finally(() => {
          setLoadingForm(false);
        });
    } else {
      categoryRepository
        .createCategory(apiData)
        .then(() => {
          toast.success(t('categories:create_success'));
          setSuccessForm(true);
          setTimeout(() => {
            navigate(routes.subcategories.path);
          }, 1500);
        })
        .catch((err: Error) => {
          console.log('Error creating category:', err);
          setErrorForm(err.message);
          toast.error(err.message || t('categories:create_error'));
        })
        .finally(() => {
          setLoadingForm(false);
        });
    }
  }

  return (
    <M.Loading loading={loading} error={null} fullWiewport>
      <T.Container py="50">
        <A.Title as="h1" fontSize="48" mb="30" fw="black" textAlign="center">
          {categoryId && categoryId !== 'new-subcategory'
            ? t('categories:edit_subcategory')
            : t('categories:create_a_new_subcategory')}
        </A.Title>

        <T.Flex gap="5" valign="flex-start" halign="center">
          {/* Form Section - Left Column */}
          <T.Col maxWidth="500px">
            <T.Block p="25" borderRadius="8" bg="white">
              <form onSubmit={handleSubmit(onSubmit)} noValidate>
                <T.Block mb="20">
                  <InputText
                    label={t('categories:topicCode')}
                    name="topicCode"
                    type="text"
                    control={control}
                    disabled={false}
                    required
                  />
                </T.Block>
                <T.Block mb="20">
                  <InputText
                    label={t('categories:code')}
                    name="code"
                    type="text"
                    control={control}
                    disabled={false}
                    required
                  />
                </T.Block>
                <T.Block mb="20">
                  <InputText
                    label={t('categories:label')}
                    name="label"
                    type="text"
                    control={control}
                    disabled={false}
                    required
                  />
                </T.Block>
                <T.Block mb="20">
                  <InputText
                    label={t('categories:filter')}
                    name="filter"
                    type="text"
                    control={control}
                    disabled={false}
                    required
                  />
                </T.Block>
                <T.Block mb="20">
                  <InputText
                    label={t('categories:label_english')}
                    name="label_en"
                    type="text"
                    control={control}
                    disabled={false}
                    required
                  />
                </T.Block>
                <T.Block mb="20">
                  <Controller
                    name="parent_id"
                    control={control}
                    render={({ field }) => (
                      <A.InputAutocomplete
                        {...field}
                        label={t('categories:parent_category')}
                        name="parent_id"
                        options={[
                          ...categories?.map((item) => {
                            return {
                              value: item.id,
                              label: item.label,
                            };
                          }),
                        ]}
                      />
                    )}
                  />
                </T.Block>
                <T.Block mb="20">
                  <A.Text fontSize="14" mb="10" color="grey-80">
                    {t('categories:type')}{' '}
                    <span style={{ color: '#e74c3c' }}>*</span>
                  </A.Text>
                  <Controller
                    name="type"
                    control={control}
                    render={({ field }) => (
                      <>
                        <div
                          style={{
                            display: 'flex',
                            gap: '12px',
                            flexWrap: 'wrap',
                            marginBottom: '8px',
                          }}
                        >
                          {Object.values(templateData).map((template) => (
                            <div
                              key={template.value}
                              onClick={() => {
                                field.onChange(template.value);
                                setSelectedTemplate(template.value);
                              }}
                              style={{
                                cursor: 'pointer',
                                border:
                                  field.value === template.value ||
                                  (!field.value &&
                                    template.value === 'template_one')
                                    ? '3px solid #007bff'
                                    : '2px solid #e0e0e0',
                                borderRadius: '8px',
                                padding: '12px',
                                flex: '1',
                                minWidth: '140px',
                                textAlign: 'center',
                                backgroundColor:
                                  field.value === template.value ||
                                  (!field.value &&
                                    template.value === 'template_one')
                                    ? '#f8f9fa'
                                    : 'white',
                                transition: 'all 0.3s ease',
                                boxShadow:
                                  field.value === template.value ||
                                  (!field.value &&
                                    template.value === 'template_one')
                                    ? '0 4px 12px rgba(0,123,255,0.2)'
                                    : '0 2px 6px rgba(0,0,0,0.08)',
                                transform:
                                  field.value === template.value ||
                                  (!field.value &&
                                    template.value === 'template_one')
                                    ? 'translateY(-2px)'
                                    : 'translateY(0)',
                              }}
                              onMouseEnter={(e) => {
                                if (field.value !== template.value) {
                                  e.currentTarget.style.transform =
                                    'translateY(-1px)';
                                  e.currentTarget.style.boxShadow =
                                    '0 4px 8px rgba(0,0,0,0.12)';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (field.value !== template.value) {
                                  e.currentTarget.style.transform =
                                    'translateY(0)';
                                  e.currentTarget.style.boxShadow =
                                    '0 2px 6px rgba(0,0,0,0.08)';
                                }
                              }}
                            >
                              <div
                                style={{
                                  width: '100%',
                                  height: '80px',
                                  borderRadius: '4px',
                                  overflow: 'hidden',
                                  marginBottom: '8px',
                                  border: '1px solid #f0f0f0',
                                }}
                              >
                                <img
                                  src={template.image}
                                  alt={template.label}
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    display: 'block',
                                  }}
                                />
                              </div>
                              <A.Text mb="3" color="grey-90">
                                {template.label}
                              </A.Text>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  />
                </T.Block>

                <T.Block mt="30" mb="20">
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      gap: '10px',
                    }}
                  >
                    <A.Button
                      type="submit"
                      bg="success"
                      loading={loadingForm}
                      disabled={loadingForm}
                    >
                      {categoryId && categoryId !== 'new-subcategory'
                        ? t('common:update')
                        : t('common:create')}
                    </A.Button>
                  </div>
                </T.Block>

                <T.Block mt="20">
                  <A.Alert alert="success" side="🎉" mb="15">
                    {successForm &&
                      (categoryId && categoryId !== 'new-universe'
                        ? t('categories:update_success')
                        : t('categories:create_success'))}
                  </A.Alert>
                  <A.Alert alert="error" side="⚠️" mb="15">
                    {errorForm && <A.InnerHTML html={errorForm} />}
                  </A.Alert>
                </T.Block>
              </form>
            </T.Block>
          </T.Col>

          {/* Template Preview Section - Right Column */}
          <T.Col maxWidth="500px">
            <T.Block p="20" borderRadius="8" bg="white">
              <A.Text fontSize="18" mb="15" textAlign="center" color="grey-90">
                {t('categories:template_preview')}
              </A.Text>

              {selectedTemplate &&
              templateData[selectedTemplate as keyof typeof templateData] ? (
                <div style={{ textAlign: 'center' }}>
                  <T.Block mb="15">
                    <A.Text fontSize="16" mb="5" color="primary">
                      {
                        templateData[
                          selectedTemplate as keyof typeof templateData
                        ].label
                      }
                    </A.Text>
                  </T.Block>

                  <T.Block borderRadius="8" overflow="hidden" p="10">
                    <img
                      src={
                        templateData[
                          selectedTemplate as keyof typeof templateData
                        ].image
                      }
                      alt={`${
                        templateData[
                          selectedTemplate as keyof typeof templateData
                        ].label
                      } Preview`}
                      style={{
                        width: '100%',
                        height: 'auto',
                        display: 'block',
                        borderRadius: '6px',
                        maxHeight: '600px',
                        objectFit: 'contain',
                      }}
                    />
                  </T.Block>
                </div>
              ) : (
                <T.Block py="40">
                  <A.Text fontSize="14" color="grey-60">
                    Select a template to see preview
                  </A.Text>
                </T.Block>
              )}
            </T.Block>
          </T.Col>
        </T.Flex>
      </T.Container>
    </M.Loading>
  );
}

export default SubCategoryFormPage;
